import { isDefined } from "@clipboard-health/util-ts";
import { useImpersonation } from "@src/appV2/Auth/Impersonation/context";
import { DeploymentEnvironmentName, environmentConfig } from "@src/appV2/environment";
import { isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { type WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { useCallback } from "react";

export function useOnClockOut({
  shift,
  clockOutModalState,
  tooFarAwayModalState,
  inside,
  onWillOpenClockOutModal,
  onClockOutInitiated,
  shouldShowSuggestInstantPay,
  onSuggestInstantPay,
}: {
  shift: WorkerShift;
  clockOutModalState: UseModalState;
  tooFarAwayModalState: UseModalState;
  inside: boolean;
  onWillOpenClockOutModal?: () => void;
  onClockOutInitiated?: () => void;
  shouldShowSuggestInstantPay?: boolean;
  onSuggestInstantPay?: () => void;
}): { onClockOut: () => void; onProceedToClockOut: () => void } {
  const { showErrorToast } = useToast();
  const { isImpersonating } = useImpersonation();

  const isClockOutForbidden =
    !isCapacitorPlatform() &&
    !isImpersonating &&
    environmentConfig.REACT_APP_ENVIRONMENT_NAME === DeploymentEnvironmentName.PRODUCTION;

  const onProceedToClockOut = useCallback(() => {
    onWillOpenClockOutModal?.();
    clockOutModalState.openModal();
  }, [onWillOpenClockOutModal, clockOutModalState]);

  const onClockOut = useCallback(() => {
    if (isClockOutForbidden) {
      showErrorToast("Clock out is only available on the mobile app.");
      return;
    }

    onClockOutInitiated?.();

    logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_CLOCK_OUT_TAPPED, { shiftId: shift._id });

    // Check geofence status before opening clock out modal
    if (!inside) {
      logEvent(APP_V2_USER_EVENTS.CLOCK_OUT_OUTSIDE_GEOFENCE, {
        shiftId: shift._id,
        workerId: shift.agentId,
        workplaceId: shift.facilityId,
      });
      tooFarAwayModalState.openModal();
      return;
    }

    if (shouldShowSuggestInstantPay && isDefined(onSuggestInstantPay)) {
      onSuggestInstantPay();
      return;
    }

    onProceedToClockOut();
  }, [
    isClockOutForbidden,
    onClockOutInitiated,
    shift._id,
    shift.agentId,
    shift.facilityId,
    inside,
    showErrorToast,
    tooFarAwayModalState,
    onSuggestInstantPay,
    shouldShowSuggestInstantPay,
    onProceedToClockOut,
  ]);

  return { onClockOut, onProceedToClockOut };
}
